import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, PawPrint } from 'lucide-react';
import { usePetCategoryCounts } from '@/hooks/usePublicPets';
import { Skeleton } from '@/components/ui/skeleton';

import allPetsImg from '@/assets/categories/all-pets.jpg';
import dogsImg from '@/assets/categories/dogs.jpg';
import catsImg from '@/assets/categories/cats.jpg';
import birdsImg from '@/assets/categories/birds.jpg';
import fishImg from '@/assets/categories/fish.jpg';
import reptilesImg from '@/assets/categories/reptiles.jpg';
import smallPetsImg from '@/assets/categories/small-pets.jpg';
import horsesImg from '@/assets/categories/horses.jpg';
import cattleImg from '@/assets/categories/cattle.jpg';
import otherImg from '@/assets/categories/other.jpg';

const PetCategories = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: categoryCounts, isLoading } = usePetCategoryCounts();

  const categories = [
    { id: 'all', name: t('categories.allPets', 'All Pets'), image: allPetsImg },
    { id: 'dog', name: t('categories.dogs', 'Dogs'), image: dogsImg },
    { id: 'cat', name: t('categories.cats', 'Cats'), image: catsImg },
    { id: 'bird', name: t('categories.birds', 'Birds'), image: birdsImg },
    { id: 'fish', name: t('categories.fish', 'Fish'), image: fishImg },
    { id: 'reptile', name: t('categories.reptiles', 'Reptiles'), image: reptilesImg },
    { id: 'small_pet', name: t('categories.smallPets', 'Small Pets'), image: smallPetsImg },
    { id: 'horse', name: t('categories.horses', 'Horses'), image: horsesImg },
    { id: 'cattle', name: t('categories.cattle', 'Cattle'), image: cattleImg },
    { id: 'other', name: t('categories.other', 'Other'), image: otherImg },
  ];

  return (
    <div className="max-w-3xl mx-auto px-4 pt-4 pb-24 space-y-6">
      <header className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/explore'))}
          aria-label={t('common.back', 'Back')}
          className="flex h-10 w-10 items-center justify-center rounded-full bg-card border border-border hover:bg-muted active:scale-95 transition"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
        <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-br from-violet-500 to-fuchsia-600 text-white shadow-md">
          <PawPrint className="h-5 w-5" />
        </span>
        <div>
          <h1 className="text-xl font-bold leading-tight">
            {t('categories.petCategories', 'Pet Categories')}
          </h1>
          <p className="text-xs text-muted-foreground">
            {t('categories.subtitle', 'Browse pets by species')}
          </p>
        </div>
      </header>

      {isLoading ? (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
          {Array.from({ length: 10 }).map((_, i) => (
            <Skeleton key={i} className="aspect-square rounded-2xl" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
          {categories.map((cat) => {
            const count = categoryCounts?.[cat.id] || 0;
            return (
              <Link
                key={cat.id}
                to={`/pets/category/${cat.id}`}
                className="group flex flex-col items-center text-center"
              >
                <div className="relative w-full aspect-square rounded-2xl overflow-hidden mb-1.5 shadow-sm group-hover:shadow-md transition-shadow ring-1 ring-black/5">
                  <img
                    src={cat.image}
                    alt={cat.name}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                  />
                  <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/55 to-transparent" />
                  <div className="absolute inset-x-0 bottom-1 text-center text-white text-xs font-semibold drop-shadow">
                    {cat.name}
                  </div>
                </div>
                {count > 0 && (
                  <span className="text-[10px] text-muted-foreground leading-tight">
                    {t('categories.petCount', { count })}
                  </span>
                )}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PetCategories;
